import { noteIndex, getNoteName, usesFlats, ALL_KEYS, DEGREE_LABELS } from './theory';
import { getGuitarTitle, notesHeading } from './notes-row';

// ── Tuning & Layout Constants ─────────────────────────────────────────────────

// Standard 4-string upright bass tuning, stored as semitone indices
// string 0 = E (lowest, 4), 1 = A (9), 2 = D (2), 3 = G (7) (highest)
export const UPRIGHT_BASS_TUNING = [4, 9, 2, 7];

// Number of fingered positions (semitones from nut) to render.
// Position 0 = open string (rendered above nut); positions 1-12 = fingered.
export const TOTAL_POSITIONS_BASS = 12;

// Positions where physical side-marker dots appear (mimics tape used by bluegrass bassists).
export const POSITION_MARKERS_BASS = [3, 5, 7, 10, 12];

// ── Degree & Formula Tables ───────────────────────────────────────────────────

// Scale degree labels — the full chromatic map shared with the guitar ('b3', 'b5', ...),
// so Intervals on the board, the notes row and the other instruments all agree.
export const BASS_DEGREE_LABELS = DEGREE_LABELS;

// Scale/arpeggio formulas — semitone intervals from root.
// Tonality (Major/Minor) is applied via resolveBassFormula() below, matching
// the CLAUDE.md tonality convention: never include Major/Minor in structure lists.
export const BASS_FORMULAS = {
  'Major':          [0, 2, 4, 5, 7, 9, 11],
  'Minor':          [0, 2, 3, 5, 7, 8, 10],      // natural minor
  'Maj Pentatonic': [0, 2, 4, 7, 9],
  'Min Pentatonic': [0, 3, 5, 7, 10],
  'Root & 5th':     [0, 7],
  'Triad Major':    [0, 4, 7],
  'Triad Minor':    [0, 3, 7],
  'Arp Major 7':    [0, 4, 7, 11],
  'Arp Minor 7':    [0, 3, 7, 10],
  'Arp Dom 7':      [0, 4, 7, 10],
  'Chromatic':      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export const BASS_STRUCTURE_OPTIONS = [
  'Full Scale', 'Pentatonic', 'Root & 5th', 'Triad', 'Chromatic',
];

// Chord Changes degree colors — CSS variable references for hsl() usage.
// Anchored to existing brand colors: sky=active strings, amber=guitar roots,
// violet=guitar scale notes. I-IV-V echo these three for muscle-memory continuity.
export const CHORD_DEGREE_COLORS = {
  1: { fillVar: '--chord-i',   name: 'sky'    },
  2: { fillVar: '--chord-ii',  name: 'teal'   },
  3: { fillVar: '--chord-iii', name: 'rose'   },
  4: { fillVar: '--chord-iv',  name: 'amber'  },
  5: { fillVar: '--chord-v',   name: 'violet' },
  6: { fillVar: '--chord-vi',  name: 'orange' },
  7: { fillVar: '--chord-vii', name: 'zinc'   },
};

// ── Canonical Bluegrass Root+5th Fingering Lookup ────────────────────────────
//
// One practical fingering per root, codifying how bluegrass bassists actually
// play root-5 patterns. Do not compute dynamically — these are aesthetic choices.
//
// strings: 0 = E, 1 = A, 2 = D, 3 = G
// pos: 0 = open string
//
// Strategy values:
//   'open-5below'   — both notes open strings; 5th is on adjacent lower string.
//   'open-5above'   — root is open, 5 is fingered on next-higher string (E-string roots only).
//   'closed-5below' — both fingered; 5th is on adjacent lower string at same fret as root.
//   'closed-5above' — both fingered; 5 is on next-higher string (E-string roots only).
//
// Preference hierarchy:
//   1. Use open strings whenever root or 5th land on an open string.
//   2. Prefer 5th on the adjacent LOWER string at the same fret (natural rocking motion,
//      same hand position). Bass players "walk" the 1 and 5 above and below each other.
//   3. When root is on the E string (lowest), 5th must ascend to the A string — no lower
//      option exists. This applies to E, F, F#, G#, and their enharmonics.
export const BLUEGRASS_ROOT_FIFTH_FINGERINGS = {
  // Root on A string (string 1) — 5th falls on adjacent lower E string (string 0), same fret
  'C':  { rootString: 1, rootPos: 3, fifthString: 0, fifthPos: 3, strategy: 'closed-5below' },
  'C#': { rootString: 1, rootPos: 4, fifthString: 0, fifthPos: 4, strategy: 'closed-5below' },
  'Db': { rootString: 1, rootPos: 4, fifthString: 0, fifthPos: 4, strategy: 'closed-5below' },
  // Root on D string (string 2) — 5th falls on adjacent lower A string (string 1), same fret
  'D':  { rootString: 2, rootPos: 0, fifthString: 1, fifthPos: 0, strategy: 'open-5below'   },
  'D#': { rootString: 2, rootPos: 1, fifthString: 1, fifthPos: 1, strategy: 'closed-5below' },
  'Eb': { rootString: 2, rootPos: 1, fifthString: 1, fifthPos: 1, strategy: 'closed-5below' },
  // Root on E string (string 0) — no lower string; 5th must ascend to A string (string 1)
  'E':  { rootString: 0, rootPos: 0, fifthString: 1, fifthPos: 2, strategy: 'open-5above'   },
  'F':  { rootString: 0, rootPos: 1, fifthString: 1, fifthPos: 3, strategy: 'closed-5above' },
  'F#': { rootString: 0, rootPos: 2, fifthString: 1, fifthPos: 4, strategy: 'closed-5above' },
  'Gb': { rootString: 0, rootPos: 2, fifthString: 1, fifthPos: 4, strategy: 'closed-5above' },
  'G#': { rootString: 0, rootPos: 4, fifthString: 1, fifthPos: 6, strategy: 'closed-5above' },
  'Ab': { rootString: 0, rootPos: 4, fifthString: 1, fifthPos: 6, strategy: 'closed-5above' },
  // Root on G string (string 3) — 5th falls on adjacent lower D string (string 2), open
  'G':  { rootString: 3, rootPos: 0, fifthString: 2, fifthPos: 0, strategy: 'open-5below'   },
  // Root on A string (string 1) open — 5th (E) on adjacent lower E string (string 0), open
  'A':  { rootString: 1, rootPos: 0, fifthString: 0, fifthPos: 0, strategy: 'open-5below'   },
  'A#': { rootString: 1, rootPos: 1, fifthString: 0, fifthPos: 1, strategy: 'closed-5below' },
  'Bb': { rootString: 1, rootPos: 1, fifthString: 0, fifthPos: 1, strategy: 'closed-5below' },
  'B':  { rootString: 1, rootPos: 2, fifthString: 0, fifthPos: 2, strategy: 'closed-5below' },
};

// ── Theory Functions ──────────────────────────────────────────────────────────

/**
 * Build the full fingerboard matrix for rendering.
 *
 * Returns Note[][] — shape [4 strings][13 positions: 0=open … 12=octave].
 * Each Note: { noteName, interval, scaleDegree, isRoot, isInScale, stringIndex, positionIndex }
 *
 * @param {string}   key     - root key name, e.g. 'G'
 * @param {number[]} formula - semitone intervals from root
 * @param {number[]} tuning  - semitone indices for each open string
 * @param {string}   [tonality] - 'Major' | 'Minor', only used to pick sharps or flats for
 *   formulas that are not a full seven-note mode (pentatonic, triad, chromatic)
 */
export function buildFingerboardMatrix(key, formula, tuning = UPRIGHT_BASS_TUNING, tonality = 'Major') {
  const rootIdx = noteIndex(key);
  // Determine accidental preference for the current key/formula combination
  const heptatonic = formula.length === 7;
  const flats = usesFlats(key, heptatonic ? formula : null, tonality === 'Minor' ? 'minor' : 'major');

  return tuning.map((nutPitch, stringIndex) => {
    const positions = [];
    for (let pos = 0; pos <= TOTAL_POSITIONS_BASS; pos++) {
      const noteIdx   = (nutPitch + pos) % 12;
      const interval  = (noteIdx - rootIdx + 12) % 12;
      const isInScale = formula.includes(interval);
      positions.push({
        noteName:      getNoteName(noteIdx, flats),
        interval,
        scaleDegree:   BASS_DEGREE_LABELS[interval] ?? '',
        isRoot:        interval === 0,
        isInScale,
        stringIndex,
        positionIndex: pos,
      });
    }
    return positions;
  });
}

/**
 * For "Root & 5th" with Chord Changes OFF: all positions of the root and 5th
 * across the active strings.
 *
 * @param {string}    key          - root key name
 * @param {boolean[]} stringFocus  - which strings are active
 * @returns {Array<{string, position, isRoot, interval, noteName}>}
 */
export function buildRootFifthAllPositions(key, stringFocus) {
  const formula = BASS_FORMULAS['Root & 5th']; // [0, 7]
  const matrix  = buildFingerboardMatrix(key, formula);
  const nodes   = [];

  matrix.forEach((stringPositions, stringIdx) => {
    if (!stringFocus[stringIdx]) return;
    stringPositions.forEach(note => {
      if (note.isInScale) {
        nodes.push({
          string:   stringIdx,
          position: note.positionIndex,
          isRoot:   note.isRoot,
          interval: note.interval,
          noteName: note.noteName,
        });
      }
    });
  });

  return nodes;
}

/**
 * For "Root & 5th" with Chord Changes ON: one practical root+5 pair per
 * selected scale degree, drawn from BLUEGRASS_ROOT_FIFTH_FINGERINGS.
 *
 * @param {string}   key             - song key (e.g. 'G')
 * @param {number[]} selectedDegrees - 1-indexed scale degrees, e.g. [1, 4, 5]
 * @returns {Array<{degree, chordRoot, rootNode, fifthNode}>}
 */
export function buildChordChangesFingerings(key, selectedDegrees) {
  const keyFormula = BASS_FORMULAS['Major'];
  const rootIdx    = noteIndex(key);
  const flats      = usesFlats(key, keyFormula, 'major');

  // Scale notes 0-indexed: scaleNotes[0] = tonic, scaleNotes[6] = leading tone
  const scaleNotes = keyFormula.map(semi => getNoteName((rootIdx + semi) % 12, flats));

  const result = [];
  selectedDegrees.forEach(deg => {
    const chordRoot = scaleNotes[deg - 1];
    const fingering = BLUEGRASS_ROOT_FIFTH_FINGERINGS[chordRoot];
    if (!fingering) return; // enharmonic missing — shouldn't happen with the full table

    const chordRootIdx = noteIndex(chordRoot);
    const fifthIdx     = (chordRootIdx + 7) % 12;

    const rootNode = {
      string:   fingering.rootString,
      position: fingering.rootPos,
      isRoot:   true,
      interval: 0,
      noteName: getNoteName(chordRootIdx, flats),
    };

    const fifthNode = {
      string:   fingering.fifthString,
      position: fingering.fifthPos,
      isRoot:   false,
      interval: 7,
      noteName: getNoteName(fifthIdx, flats),
    };

    result.push({ degree: deg, chordRoot, rootNode, fifthNode });
  });

  return result;
}

/**
 * Resolve the correct scale/arpeggio formula from structure + tonality.
 * Follows CLAUDE.md tonality convention — tonality must never appear in the
 * structure option list; it is always a separate toggle.
 *
 * @param {string} structure - one of BASS_STRUCTURE_OPTIONS
 * @param {string} tonality  - 'Major' | 'Minor'
 * @returns {number[]} semitone interval array
 */
export function resolveBassFormula(structure, tonality) {
  const isMajor = tonality === 'Major';
  switch (structure) {
    case 'Full Scale':  return BASS_FORMULAS[isMajor ? 'Major' : 'Minor'];
    case 'Pentatonic':  return BASS_FORMULAS[isMajor ? 'Maj Pentatonic' : 'Min Pentatonic'];
    case 'Root & 5th':  return BASS_FORMULAS['Root & 5th'];
    case 'Triad':       return BASS_FORMULAS[isMajor ? 'Triad Major' : 'Triad Minor'];
    case 'Chromatic':   return BASS_FORMULAS['Chromatic'];
    default:            return BASS_FORMULAS['Major'];
  }
}

// ── Page title and notes row ──────────────────────────────────────────────────

/**
 * Page title for the bass: "G Major Scale", "G Minor Pentatonic", "G Root & 5th",
 * "G Major Triad", "G Chromatic".
 *
 * @param {object} p
 * @param {string} p.key
 * @param {string} p.tonality 'Major' | 'Minor'
 * @param {string} p.structure one of BASS_STRUCTURE_OPTIONS
 * @returns {string}
 */
export function getBassTitle({ key, tonality, structure }) {
  const ton = tonality === 'Minor' ? 'Minor' : 'Major';
  if (structure === 'Root & 5th') return `${key} Root & 5th`;
  if (structure === 'Triad') return `${key} ${ton} Triad`;
  if (structure === 'Chromatic') return `${key} Chromatic`;
  return getGuitarTitle({ key, tonality, structure });
}

/**
 * The notes row for the bass (heading from notesHeading). Names are spelled exactly as the fingerboard
 * spells them (same sharps/flats choice as buildFingerboardMatrix), so the row always
 * matches the dots.
 *
 * @returns {{heading: string, notes: Array<{name: string, degree: string, isRoot: boolean}>}}
 */
export function getBassNotesRow({ key, tonality, structure }) {
  const rootIdx = noteIndex(key);
  const formula = resolveBassFormula(structure, tonality);
  if (rootIdx === -1) return { heading: notesHeading(structure), notes: [] };
  const flats = usesFlats(key, formula.length === 7 ? formula : null, tonality === 'Minor' ? 'minor' : 'major');
  const notes = formula.map(interval => ({
    name:   getNoteName((rootIdx + interval) % 12, flats),
    degree: DEGREE_LABELS[interval],
    isRoot: interval === 0,
  }));
  return { heading: notesHeading(structure), notes };
}

// Re-export ALL_KEYS so consuming components only need one import source.
export { ALL_KEYS };
